const HEX_COLOR_REGEX = /^#([0-9a-f]{6})$/i;
const SHORT_HEX_COLOR_REGEX = /^#([0-9a-f]{3})$/i;
const RGB_FUNCTION_REGEX =
  /^rgba?\(\s*(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*(?:,|\/|\s)\s*(0|0?\.\d+|1(\.0+)?))?\s*\)$/i;
const RGB_VALUE_REGEX =
  /^(\d{1,3})(?:\s+|\s*,\s*)(\d{1,3})(?:\s+|\s*,\s*)(\d{1,3})$/;
const DEFAULT_BRAND_COLOR = '#29a2a7';
const DEFAULT_BRAND_COLOR_HEX = DEFAULT_BRAND_COLOR.toLowerCase();

const BRAND_PALETTE_VARIABLES = [
  '--brand-color',
  '--woot-500',
  '--woot-400',
  '--woot-300',
  '--woot-200',
  '--woot-100',
  '--woot-75',
  '--woot-50',
  '--woot-25',
  '--woot-600',
  '--woot-700',
  '--woot-800',
  '--woot-850',
  '--woot-900',
];

const BRAND_ALIAS_VARIABLES = [
  '--blue-1',
  '--blue-2',
  '--blue-3',
  '--blue-4',
  '--blue-5',
  '--blue-6',
  '--blue-7',
  '--blue-8',
  '--blue-9',
  '--blue-10',
  '--blue-11',
  '--blue-12',
  '--text-blue',
];

const BRAND_SOLID_VARIABLES = [
  '--brand-solid-1',
  '--brand-solid-2',
  '--brand-solid-3',
  '--brand-solid-active',
  '--brand-solid-blue',
];

const BRAND_SOLID_DARK_VARIABLES = [
  '--brand-solid-1-dark',
  '--brand-solid-2-dark',
  '--brand-solid-3-dark',
  '--brand-solid-active-dark',
  '--brand-solid-blue-dark',
];

const SOLID_VARIABLES = [
  '--solid-1',
  '--solid-2',
  '--solid-3',
  '--solid-active',
  '--solid-blue',
  '--solid-1-dark',
  '--solid-2-dark',
  '--solid-3-dark',
  '--solid-active-dark',
  '--solid-blue-dark',
];

const BRAND_VARIABLES = [
  ...BRAND_PALETTE_VARIABLES,
  ...BRAND_ALIAS_VARIABLES,
  ...BRAND_SOLID_VARIABLES,
  ...BRAND_SOLID_DARK_VARIABLES,
  ...SOLID_VARIABLES,
  '--brand-color-hex',
];

const TEAL_SCALE = [
  ['--teal-1', 0.86],
  ['--teal-2', 0.72],
  ['--teal-3', 0.6],
  ['--teal-4', 0.48],
  ['--teal-5', 0.36],
  ['--teal-6', 0.26],
  ['--teal-7', 0.18],
  ['--teal-8', 0.08],
  ['--teal-9', 0],
  ['--teal-10', -0.12],
  ['--teal-11', -0.24],
  ['--teal-12', -0.36],
];

const isValidRgbChannel = value => value >= 0 && value <= 255;

const buildColorObject = (r, g, b) => {
  if (![r, g, b].every(isValidRgbChannel)) {
    return null;
  }

  const toHex = component => component.toString(16).padStart(2, '0');

  return {
    r,
    g,
    b,
    hex: `#${toHex(r)}${toHex(g)}${toHex(b)}`,
  };
};

const parseColor = color => {
  if (!color || typeof color !== 'string') {
    return null;
  }

  const normalizedColor = color.trim();

  const hexMatch = HEX_COLOR_REGEX.exec(normalizedColor);

  if (hexMatch) {
    const [, hex] = hexMatch;

    return buildColorObject(
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16)
    );
  }

  const shortHexMatch = SHORT_HEX_COLOR_REGEX.exec(normalizedColor);

  if (shortHexMatch) {
    const [, hex] = shortHexMatch;
    const expandedHex = hex
      .split('')
      .map(component => component.repeat(2))
      .join('');

    return buildColorObject(
      parseInt(expandedHex.slice(0, 2), 16),
      parseInt(expandedHex.slice(2, 4), 16),
      parseInt(expandedHex.slice(4, 6), 16)
    );
  }

  const rgbFunctionMatch = RGB_FUNCTION_REGEX.exec(normalizedColor);

  if (rgbFunctionMatch) {
    const [, r, g, b] = rgbFunctionMatch;

    return buildColorObject(Number(r), Number(g), Number(b));
  }

  const rgbValueMatch = RGB_VALUE_REGEX.exec(normalizedColor);

  if (rgbValueMatch) {
    const [, r, g, b] = rgbValueMatch;

    return buildColorObject(Number(r), Number(g), Number(b));
  }

  return null;
};

const resolveBrandColorInput = color => {
  if (color) {
    return color;
  }

  if (typeof window !== 'undefined') {
    return window?.globalConfig?.BRAND_PRIMARY_COLOR || DEFAULT_BRAND_COLOR;
  }

  return DEFAULT_BRAND_COLOR;
};

const resolveBrandColor = color => parseColor(resolveBrandColorInput(color));

export const normalizeColorHex = color => parseColor(color)?.hex || null;

const getRgbValue = color => {
  const resolvedColor = resolveBrandColor(color);

  if (!resolvedColor) {
    return null;
  }

  return {
    ...resolvedColor,
    rgbValue: `${resolvedColor.r} ${resolvedColor.g} ${resolvedColor.b}`,
  };
};

export const getBrandColorHex = color => getRgbValue(color)?.hex || null;

export const getBrandColorRgb = color => {
  const rgbColor = getRgbValue(color);

  return rgbColor ? `rgb(${rgbColor.rgbValue})` : null;
};

export const getBrandColorAlpha = (alpha, color) => {
  const rgbColor = getRgbValue(color);

  if (!rgbColor) {
    return null;
  }

  const resolvedAlpha = alpha ?? 1;

  return `rgb(${rgbColor.rgbValue} / ${resolvedAlpha})`;
};

export const getAppliedBrandColorHex = () => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;

    if (root) {
      const inlineValue = root.style?.getPropertyValue('--brand-color-hex');
      const computedStyle =
        typeof window !== 'undefined' && window?.getComputedStyle
          ? window.getComputedStyle(root)
          : null;
      const computedValue = computedStyle?.getPropertyValue('--brand-color-hex');
      const normalized = inlineValue?.trim() || computedValue?.trim();
      const hex = normalizeColorHex(normalized);

      if (hex) {
        return hex;
      }
    }
  }

  return getBrandColorHex();
};

const clampChannel = value =>
  Math.min(255, Math.max(0, Math.round(value)));

const interpolateChannel = (start, end, amount) =>
  clampChannel(start + (end - start) * amount);

const shiftColor = (color, target, amount) => ({
  r: interpolateChannel(color.r, target, amount),
  g: interpolateChannel(color.g, target, amount),
  b: interpolateChannel(color.b, target, amount),
});

const mixColors = (base, color, amount) => ({
  r: interpolateChannel(base.r, color.r, amount),
  g: interpolateChannel(base.g, color.g, amount),
  b: interpolateChannel(base.b, color.b, amount),
});

const lightenColor = (color, amount) => shiftColor(color, 255, amount);
const darkenColor = (color, amount) => shiftColor(color, 0, amount);

const toCssRgb = ({ r, g, b }) => `${r} ${g} ${b}`;

const setRgbVariable = (targets, variable, value) => {
  targets.forEach(target => {
    target?.style?.setProperty(variable, toCssRgb(value));
  });
};

const updateMetaThemeColor = color => {
  if (typeof document === 'undefined') {
    return;
  }

  ['meta[name="theme-color"]', 'meta[name="msapplication-TileColor"]'].forEach(
    selector => {
      document.querySelectorAll(selector).forEach(node => {
        node.setAttribute('content', color);
      });
    }
  );
};

export const applyCustomBrandColor = color => {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const body = document.body;
  const targets = [root, body].filter(Boolean);
  const clearBrandPalette = () => {
    BRAND_VARIABLES.forEach(variable => {
      targets.forEach(target => {
        target?.style?.removeProperty(variable);
      });
    });
  };

  const rgbColor = getRgbValue(color);
  const normalizedHex = rgbColor?.hex?.toLowerCase?.();
  const isDefaultColor = normalizedHex === DEFAULT_BRAND_COLOR_HEX;

  clearBrandPalette();

  if (!rgbColor || isDefaultColor) {
    updateMetaThemeColor(rgbColor?.hex || DEFAULT_BRAND_COLOR);
    return;
  }

  const brandPalette = [
    ['--brand-color', rgbColor],
    ['--woot-500', rgbColor],
    ['--woot-400', lightenColor(rgbColor, 0.06962373204459427)],
    ['--woot-300', lightenColor(rgbColor, 0.16489510723758538)],
    ['--woot-200', lightenColor(rgbColor, 0.3315514962711224)],
    ['--woot-100', lightenColor(rgbColor, 0.4982078853046595)],
    ['--woot-75', lightenColor(rgbColor, 0.6684485037288775)],
    ['--woot-50', lightenColor(rgbColor, 0.8333436109664629)],
    ['--woot-25', lightenColor(rgbColor, 1)],
    ['--woot-600', darkenColor(rgbColor, 0.13995616114954676)],
    ['--woot-700', darkenColor(rgbColor, 0.27791631431506153)],
    ['--woot-800', darkenColor(rgbColor, 0.41993008863333253)],
    ['--woot-850', darkenColor(rgbColor, 0.5497601604980343)],
    ['--woot-900', darkenColor(rgbColor, 0.6917739348163053)],
  ];

  const paletteMap = new Map(brandPalette);

  paletteMap.forEach((value, variable) => {
    setRgbVariable(targets, variable, value);
  });

  TEAL_SCALE.forEach(([variable, amount]) => {
    const adjustedColor =
      amount === 0
        ? rgbColor
        : amount > 0
          ? lightenColor(rgbColor, amount)
          : darkenColor(rgbColor, Math.abs(amount));
    setRgbVariable(targets, variable, adjustedColor);
  });

  const paletteAliases = [
    ['--blue-1', '--woot-25'],
    ['--blue-2', '--woot-50'],
    ['--blue-3', '--woot-75'],
    ['--blue-4', '--woot-100'],
    ['--blue-5', '--woot-200'],
    ['--blue-6', '--woot-300'],
    ['--blue-7', '--woot-400'],
    ['--blue-8', '--woot-500'],
    ['--blue-9', '--woot-500'],
    ['--blue-10', '--woot-600'],
    ['--blue-11', '--woot-700'],
    ['--blue-12', '--woot-800'],
    ['--text-blue', '--woot-500'],
  ];

  paletteAliases.forEach(([variable, source]) => {
    const value = paletteMap.get(source);

    if (value) {
      setRgbVariable(targets, variable, value);
    }
  });

  const brandSolids = [
    ['--brand-solid-active', lightenColor(rgbColor, 0.5)],
    ['--brand-solid-blue', rgbColor],
  ];

  const brandSolidMap = new Map(brandSolids);

  brandSolidMap.forEach((value, variable) => {
    setRgbVariable(targets, variable, value);
  });

  const solidMirrors = [
    ['--solid-active', brandSolidMap.get('--brand-solid-active')],
    ['--solid-blue', brandSolidMap.get('--brand-solid-blue')],
  ];

  solidMirrors.forEach(([variable, value]) => {
    if (value) {
      setRgbVariable(targets, variable, value);
    }
  });

  const brandDarkSolids = [
    [
      '--brand-solid-active-dark',
      mixColors({ r: 53, g: 57, b: 66 }, rgbColor, 0.42),
    ],
    ['--brand-solid-blue-dark', darkenColor(rgbColor, 0.41993008863333253)],
  ];

  const brandDarkSolidMap = new Map(brandDarkSolids);

  brandDarkSolidMap.forEach((value, variable) => {
    setRgbVariable(targets, variable, value);
  });

  const darkSolidMirrors = [
    ['--solid-active-dark', brandDarkSolidMap.get('--brand-solid-active-dark')],
    ['--solid-blue-dark', brandDarkSolidMap.get('--brand-solid-blue-dark')],
  ];

  darkSolidMirrors.forEach(([variable, value]) => {
    if (value) {
      setRgbVariable(targets, variable, value);
    }
  });

  targets.forEach(target => {
    target?.style?.setProperty('--brand-color-hex', rgbColor.hex);
  });

  updateMetaThemeColor(rgbColor.hex);
};

export const applyBrandPrimaryColor = applyCustomBrandColor;
export default applyBrandPrimaryColor;
